import { useMemo } from 'react';
import { create } from 'zustand';

export type ModalId =
  | 'bailout'
  | 'loanLock'
  | 'loanContacts'
  | 'dateTracking'
  | 'counteroffer'
  | 'invalidProduct'
  | 'AUS'
  | 'FG'
  | 'loanComments'
  | 'queueManager'
  | 'autocofc'
  | 'loanSummaryDialog'
  | 'boltIncidentInterceptor'
  | 'piti';

type ModalsStateStore = {
  currentModal: ModalId | null;
  openModal: (modalId: ModalId) => void;
  closeModal: () => void;
  toggleModal: (modalId: ModalId) => void;
};

export const useModalsStore = create<ModalsStateStore>()((set) => ({
  currentModal: null,
  openModal: (modalId) => set({ currentModal: modalId }),
  closeModal: () => set({ currentModal: null }),
  toggleModal: (modalId) =>
    set((state) => ({
      currentModal: state.currentModal === modalId ? null : modalId,
    })),
}));

/**
 * This hook mimics the behavior of Chakra UI's useDisclosure hook.
 *
 * @example
 * // Make sure to adjust the `ModalId` type in this file to have your modal's id there.
 * const { isOpen, onOpen, onClose } = useModalState('loanContacts');
 */
export function useModalState(modalId: ModalId) {
  const isOpen = useModalsStore((s) => s.currentModal) === modalId;
  const openModal = useModalsStore((s) => s.openModal);
  const closeModal = useModalsStore((s) => s.closeModal);
  const toggleModal = useModalsStore((s) => s.toggleModal);

  return useMemo(
    () => ({
      isOpen,
      onOpen: () => openModal(modalId),
      onClose: closeModal,
      onToggle: () => toggleModal(modalId),
    }),
    [isOpen, openModal, modalId, closeModal, toggleModal],
  );
}
